import React from 'react';
import type {
  ButtonProps,
  SxProps,
  Theme} from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  Slide,
  Typography,
  useTheme,
} from '@mui/material';
import { X as CloseIcon } from '@phosphor-icons/react/dist/ssr';

interface ProfessionalDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  variant?: 'default' | 'elevated' | 'outlined' | 'modern' | 'professional';
  sx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  showDivider?: boolean;
  autoHeight?: boolean;
  animation?: 'fade' | 'slide' | 'zoom';
  disableBackdropClick?: boolean;
  showCloseButton?: boolean;
}

export default function ProfessionalDialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  actions,
  maxWidth = 'md',
  fullWidth = true,
  variant = 'professional',
  sx,
  headerSx,
  contentSx,
  actionsSx,
  showDivider = true,
  autoHeight = false,
  animation = 'zoom',
  disableBackdropClick = false,
  showCloseButton = true,
}: ProfessionalDialogProps) {
  const theme = useTheme();

  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          boxShadow: theme.shadows[16],
          border: 'none',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '20px',
        };
      case 'outlined':
        return {
          boxShadow: theme.shadows[4],
          border:
            theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        };
      case 'modern':
        return {
          boxShadow: theme.shadows[12],
          border:
            theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
          backdropFilter: 'blur(20px)',
        };
      case 'professional':
        return {
          boxShadow:
            theme.palette.mode === 'dark'
              ? theme.shadows[12]
              : '0px 20px 40px rgba(16, 24, 40, 0.15), 0px 8px 16px rgba(16, 24, 40, 0.1)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '20px',
          backdropFilter: 'blur(24px)',
        };
      default:
        return {
          boxShadow: theme.shadows[8],
          border:
            theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        };
    }
  };

  const getAnimationComponent = () => {
    switch (animation) {
      case 'slide':
        return Slide;
      case 'fade':
        return Fade;
      case 'zoom':
      default:
        return undefined; // Use MUI's default zoom transition
    }
  };

  const AnimationComponent = getAnimationComponent();

  return (
    <Dialog
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: autoHeight ? '95vh' : '90vh',
          height: autoHeight ? 'auto' : 'auto',
          minHeight: autoHeight ? 'auto' : '50vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          ...getVariantStyles(),
          ...sx,
        },
      }}
      TransitionComponent={AnimationComponent}
      TransitionProps={
        animation === 'slide'
          ? ({ direction: 'up' } as any)
          : animation === 'fade'
            ? { timeout: { enter: 400, exit: 300 } }
            : { timeout: { enter: 300, exit: 200 } }
      }
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onClose={disableBackdropClick ? undefined : onClose}
      open={open}
    >
      {/* Header */}
      <DialogTitle
        component="div"
        sx={{
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 4 },
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, rgba(255, 255, 255, 0.8) 100%)',
          borderBottom:
            theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 2, sm: 3 },
          position: 'relative',
          flexShrink: 0,
          m: 0,
          ...headerSx,
        }}
      >
        {icon ? <Box
            sx={{
              width: { xs: 44, sm: 52 },
              height: { xs: 44, sm: 52 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.primary.contrastText,
              boxShadow: '0px 8px 24px rgba(99, 102, 241, 0.3)',
              flexShrink: 0,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            {icon}
          </Box> : null}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: subtitle ? 1 : 0,
              fontSize: { xs: '1.375rem', sm: '1.75rem', md: '2rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
            variant="h4"
          >
            {title}
          </Typography>
          {subtitle ? <Typography
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.5,
                letterSpacing: '0.01em',
              }}
              variant="body1"
            >
              {subtitle}
            </Typography> : null}
        </Box>

        {showCloseButton ? <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16 },
              right: { xs: 12, sm: 16 },
              color: 'text.secondary',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                color: 'text.primary',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              width: 32,
              height: 32,
            }}
          >
            <CloseIcon size={18} />
          </IconButton> : null}
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 4 },
          backgroundColor: 'transparent',
          flex: autoHeight ? 'none' : 1,
          overflow: autoHeight ? 'visible' : 'auto',
          minHeight: 0,
          m: 0,
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            },
          },
          ...contentSx,
        }}
      >
        <Box sx={{ mt: 2 }}>{children}</Box>
      </DialogContent>

      {/* Actions */}
      {actions ? <>
          {showDivider ? <Divider
              sx={{
                mx: { xs: 3, sm: 4 },
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              }}
            /> : null}
          <DialogActions
            sx={{
              justifyContent: 'flex-end',
              px: { xs: 3, sm: 4 },
              py: { xs: 3, sm: 4 },
              backgroundColor: 'transparent',
              borderTop: showDivider
                ? 'none'
                : theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
              m: 0,
              gap: 2,
              ...actionsSx,
            }}
          >
            {actions}
          </DialogActions>
        </> : null}
    </Dialog>
  );
}

// Helper component for common dialog actions
interface DialogActionButtonsProps {
  onCancel: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  disabled?: boolean;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  variant?: 'default' | 'destructive' | 'success';
}

export function DialogActionButtons({
  onCancel,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  disabled = false,
  confirmButtonProps,
  cancelButtonProps,
  variant = 'default',
}: DialogActionButtonsProps) {
  const theme = useTheme();

  const getConfirmButtonColor = () => {
    switch (variant) {
      case 'destructive':
        return 'error';
      case 'success':
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <>
      <Button
        disabled={loading || disabled}
        onClick={onCancel}
        sx={{
          
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.875rem',
          letterSpacing: '0.02em',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
          color: 'text.secondary',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
            borderColor: theme.palette.primary.main,
            color: 'text.primary',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
        variant="outlined"
        {...cancelButtonProps}
      >
        {cancelText}
      </Button>
      <Button
        color={getConfirmButtonColor() as any}
        disabled={loading || disabled}
        onClick={onConfirm}
        sx={{
         
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.875rem',
          letterSpacing: '0.02em',
          boxShadow: '0px 4px 12px rgba(99, 102, 241, 0.3)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 24px rgba(99, 102, 241, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
        variant="contained"
        {...confirmButtonProps}
      >
        {confirmText}
      </Button>
    </>
  );
}
